// «Actualización en curso». While a new version is being installed (server/runtime/maintenance.js)
// the whole app is covered, and once the new version answers the page reloads itself, so no tab
// keeps running old code against a newer server.
const UpdateWatch = {
    NORMAL_MS: 15000,
    UPDATING_MS: 3000,
    // A reload that has not finished after this long is stuck (e.g. it hit the service mid-restart).
    STUCK_MS: 15000,
    FIRST_MS: 2000,
    overlay: null,
    answered: 0,
    timer: null,
    loadedVersion: null,
    RELOAD_KEY: 'sicaf-update-reload',

    /**
     * Bundles this page runs (dist/app.<hash>.css and .js), as the server reports them; 'dev'
     * from source files. Read once, while loading: the login screen replaces the page body and
     * its <script> tag, and reading it then made the page reload over and over.
     */
    ownVersion() {
        if (this.loadedVersion === null) this.loadedVersion = this.readVersion();
        return this.loadedVersion;
    },

    readVersion() {
        const urls = [...document.scripts].map(script => script.src || '')
            .concat([...document.querySelectorAll('link[rel="stylesheet"]')].map(link => link.href || ''));
        const names = urls.map(url => (/\/dist\/(app\.[a-f0-9]{12}\.(?:js|css))$/.exec(url.split('?')[0]) || [])[1]).filter(Boolean);
        return names.length ? names.sort().join(' ') : 'dev';
    },

    start() {
        if (this.timer !== null) return;
        this.ownVersion();
        // Soon after loading, so a scheduled-update notice shows on entering.
        this.schedule(this.FIRST_MS);
    },

    /**
     * Safety net against reload loops: at most one automatic reload a minute, and never twice
     * towards the same version within five minutes. Without storage (private mode) it reloads.
     */
    mayReload(version) {
        try {
            const last = JSON.parse(sessionStorage.getItem(this.RELOAD_KEY) || 'null');
            const now = Date.now();
            if (last && (now - last.at < 60000 || (last.version === version && now - last.at < 300000))) return false;
            sessionStorage.setItem(this.RELOAD_KEY, JSON.stringify({ version, at: now }));
        } catch (e) {
            // No storage: reload anyway.
        }
        return true;
    },

    schedule(ms) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.check(), ms);
    },

    async check() {
        let info = null;
        try {
            const response = await fetch('/api/version', { cache: 'no-store' });
            if (response.ok) info = await response.json();
        } catch (e) {
            // The service is restarting or the network dropped: decided below.
        }
        if (info?.version && !info.maintenance && info.version !== this.ownVersion() && this.mayReload(info.version)) {
            this.reload();
            return;
        }
        if (info?.notice && !info.maintenance) window.UpdateNotice?.consider(info.notice);
        if (info?.maintenance) {
            this.show();
        } else if (this.overlay) {
            // Twice in a row, so the instant between lifting the flag and the restart never flashes the app.
            this.answered = info ? this.answered + 1 : 0;
            if (this.answered >= 2) this.hide();
        }
        this.schedule(this.overlay ? this.UPDATING_MS : this.NORMAL_MS);
    },

    /** Reloads into the new version; if that hangs, tries once more and offers a button. */
    reload() {
        window.location.reload();
        setTimeout(() => {
            // Still here: the reload is stuck. A fresh navigation usually gets through.
            window.location.replace(window.location.href);
            this.offerManualReload();
        }, this.STUCK_MS);
    },

    offerManualReload() {
        if (!this.overlay) this.show();
        const card = this.overlay?.querySelector('.update-overlay__card');
        if (!card || card.querySelector('.update-overlay__reload')) return;
        const hint = document.createElement('p');
        hint.className = 'update-overlay__text update-overlay__hint';
        hint.textContent = 'Si no avanza, pulsa este botón.';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-primary update-overlay__reload';
        button.textContent = 'Recargar ahora';
        button.addEventListener('click', () => window.location.reload());
        card.append(hint, button);
        button.focus();
    },

    show() {
        this.answered = 0;
        if (this.overlay) return;
        const overlay = document.createElement('div');
        overlay.className = 'dnr-proposal update-overlay';
        overlay.setAttribute('role', 'alertdialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'update-overlay-title');
        overlay.setAttribute('aria-describedby', 'update-overlay-text');
        overlay.tabIndex = -1;
        const card = document.createElement('div');
        card.className = 'update-overlay__card';
        const spinner = document.createElement('div');
        spinner.className = 'update-overlay__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        const eyebrow = document.createElement('p');
        eyebrow.className = 'update-overlay__eyebrow';
        eyebrow.textContent = 'SICAF';
        const title = document.createElement('h1');
        title.id = 'update-overlay-title';
        title.textContent = 'Actualización en curso';
        const text = document.createElement('p');
        text.id = 'update-overlay-text';
        text.className = 'update-overlay__text';
        text.textContent = 'SICAF volverá en unos segundos; no cierres esta página.';
        card.append(spinner, eyebrow, title, text);
        overlay.append(card);
        // Nothing behind the screen can be reached with the mouse or the keyboard.
        for (const node of document.body.children) node.inert = true;
        document.body.append(overlay);
        overlay.focus();
        this.overlay = overlay;
        this.schedule(this.UPDATING_MS);
    },

    /** Same version after the update (it was rolled back): give the app back. */
    hide() {
        if (!this.overlay) return;
        this.overlay.remove();
        this.overlay = null;
        for (const node of document.body.children) node.inert = false;
    }
};

window.UpdateWatch = UpdateWatch;
UpdateWatch.start();
